<template>
  <div part="base" class="sp-assign-specialties-modal">
    <sp-modal
      :modal-title="store.config.modalTitle"
      width="44rem"
      :open="store.isOpen"
      :closeable.prop="modalCloseable"
      :persistent.prop="store.isSubmitting"
      :no-scrollable-content.prop.camel="previewOwnsScrolling"
      @update-open="handleUpdateOpen"
      @close="handleClose"
    >
      <sp-card-content-overlay slot="content-overlay" :visible="store.isSubmitting">
        <sp-progress type="ellipsis" size="x-small"></sp-progress>
      </sp-card-content-overlay>

      <!-- The panel replaces the input step while the preview runs: sp-card-content-overlay
           is not inert, so a covered input step would still take keyboard edits, and the
           preview must be confirmed against the selection it ran on. -->
      <PreviewProgressPanel
        v-if="store.isLoading"
        :loading-text="store.labels.loading"
        :percent="store.progressPercent"
        :progress-text="store.progressText"
        :remaining-text="store.remainingText"
        :test-id="TestId.assignSpecialties.progress"
      />
      <InputStep v-else-if="store.currentStep === 'input'" key="input" />
      <ConfirmStep v-else key="confirm" />

      <ModalActions slot="actions" />
    </sp-modal>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, watch } from "vue";
import { TestId } from "#/test-ids";
import { useExpose } from "#ce/composables/useExpose";
import { toBoolean } from "#ce/utils/props";
import PreviewProgressPanel from "#ce/apps/SpAdmin/shared/components/PreviewProgressPanel.ce.vue";
import InputStep from "./components/InputStep.ce.vue";
import ConfirmStep from "./components/ConfirmStep.ce.vue";
import ModalActions from "./components/ModalActions.ce.vue";
import { props as appProps } from "./props.js";
import { useAssignSpecialtiesStore } from "./store/assign-specialties";

const emit = defineEmits(["assign-specialties-confirmed"]);

const props = defineProps(appProps);

const store = useAssignSpecialtiesStore();
const { exposeMethods } = useExpose();

// Only the final write locks the modal. The preview request can take a while,
// so users must still be able to dismiss it and cancel the server-side job.
const modalCloseable = computed(() => !store.isSubmitting);
// A ready preview with rows owns scrolling inside its table. Input, loading,
// and zero-row/error-only previews leave scrolling to the modal body.
const previewOwnsScrolling = computed(
  () => store.currentStep === "confirm" && !store.isLoading && (store.previewData?.rows?.length ?? 0) > 0,
);

function open(overrides = {}) {
  store.configure({ ...props, ...overrides });
  store.open();
}

function close() {
  store.close();
}

exposeMethods({ open, close });

watch(
  () => store.completedCount,
  (value, oldValue) => {
    if (value > (oldValue ?? 0)) {
      emit("assign-specialties-confirmed");
    }
  },
);

onMounted(() => {
  store.configure(props);

  if (toBoolean(props.autoOpen)) {
    nextTick(() => store.open());
  }
});

function handleUpdateOpen({ detail }) {
  const [value] = detail;

  if (value) {
    // sp-modal re-emits update-open(true) for an open the store initiated;
    // opening again would double-bump openGeneration per open.
    if (!store.isOpen) {
      store.open();
    }
    return;
  }

  handleClose();
}

function handleClose() {
  // Ignore dismissal while the confirmed write is in flight.
  if (store.isSubmitting) {
    return;
  }

  close();
}
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped>
.sp-assign-specialties-modal {
  display: block;

  --sp-comp-card-padding-block: 1rem;
  --sp-card-actions-padding-inline: 0;
  --sp-card-actions-padding-block: 0;
}
</style>
